import { Component, inject, OnInit, signal } from '@angular/core';
import { MatToolbarModule } from '@angular/material/toolbar';
import { MatButtonModule } from '@angular/material/button';
import { MatSidenavModule } from '@angular/material/sidenav';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatListModule } from '@angular/material/list';
import { CdkDrag, CdkDragDrop, DragDropModule } from '@angular/cdk/drag-drop';
import { MatMenuModule } from '@angular/material/menu';
import { LegTourPointConfig, TourPointConfig } from 'apps/nagel-cal-disposition/src/models/planningPageTypes';
import { DriveInstructionsDrawerService } from '../../../services/drive-instructions-drawer.service';
import { LegTourPointComponent } from '../../tour-point/leg-tour-point/leg-tour-point.component';
import { IconComponent } from '@nagel-assests';
import { ReorderDragAndDropService } from '../../../services/reorder-drag-and-drop.service';
import { MatDialog } from '@angular/material/dialog';
import { ConfirmationDialogComponent } from '@nagel-components';
import { TransportOrderStatus } from '../../../configuration/consts/pickupPlanning';
import { CalculateRoutesService } from '../../../services/calculate-routes.service';
import { PICKUP_TOUR_POINT_TYPE } from '../../../utils/tourPointsUtils';
import { MatProgressSpinner } from '@angular/material/progress-spinner';
import { getIsTransportOrderDisabled } from '../../../utils/pickupPlanningUtils';

const DELETE_TO_CONFIRMATION_DIALOG_TEXT = {
    title: $localize`Remove`,
    message: $localize`Are you sure you want to remove the selected items?`,
    confirm: $localize`Yes`,
    cancel: $localize`No`
};

@Component({
    selector: 'cal-tour-points-details-list',
    standalone: true,
    imports: [
        MatToolbarModule,
        MatButtonModule,
        MatSidenavModule,
        MatCheckboxModule,
        MatListModule,
        DragDropModule,
        MatMenuModule,
        LegTourPointComponent,
        IconComponent,
        MatProgressSpinner
    ],
    templateUrl: './tour-points-details-list.component.html',
    styleUrl: './tour-points-details-list.component.scss'
})
export class TourPointsDetailsListComponent implements OnInit {
    selectedTourPoint: TourPointConfig | null = null;
    detailsTourPoints = signal<LegTourPointConfig[]>([]);
    selectAll = signal<boolean>(false);
    private dialog = inject(MatDialog);

    isLoading = signal<boolean>(false);
    isDisabled = signal<boolean>(false);

    constructor(
        private driveInstructionsDrawerService: DriveInstructionsDrawerService,
        private dragDropService: ReorderDragAndDropService,
        private calculateRoutesService: CalculateRoutesService
    ) {}

    ngOnInit(): void {
        this.driveInstructionsDrawerService.selectedTourPointSubject.subscribe((data: TourPointConfig | null) => {
            this.selectedTourPoint = data;
        });

        this.driveInstructionsDrawerService.tourPointDetailsSubject.subscribe((data: LegTourPointConfig[]) => {
            this.selectAll.set(false);
            this.detailsTourPoints.set(data);
        });

        this.driveInstructionsDrawerService.isLoadingTourPointsSubject.subscribe((isLoading: boolean) => {
            this.isLoading.set(isLoading);
        });

        this.driveInstructionsDrawerService.transportOrderDataSubject.subscribe((value) => {
            this.isDisabled.set(getIsTransportOrderDisabled(value.status));
        });
    }

    getIsSilentUpdateInProgress(): boolean {
        return this.driveInstructionsDrawerService.isSilentUpdateInProgress();
    }

    onSelectAllChange(event: Event): void {
        this.selectAll.set((event.target as HTMLInputElement).checked);
        this.detailsTourPoints().forEach((lotTourPoint: LegTourPointConfig) => {
            lotTourPoint.checked = this.selectAll();
        });
    }

    onLegTourPointCheckedChange(event: Event, legTourPoint: LegTourPointConfig): void {
        legTourPoint.checked = (event.target as HTMLInputElement).checked;
        this.selectAll.set(this.detailsTourPoints().every((tp: LegTourPointConfig) => tp.checked));
    }

    closeDetailsDrawer(): void {
        this.driveInstructionsDrawerService.setDrawerDetailsSectionOpened(false);
        this.driveInstructionsDrawerService.setSelectedTourPoint(null);
    }

    onDrop(event: CdkDragDrop<LegTourPointConfig[]>): void {
        const {
            transportOrderId
        } = this.driveInstructionsDrawerService.transportOrderDataSubject.value;

        const isSameSection = this.isSameStaysLoadedSection(event.previousIndex, event.currentIndex);

        if (event.previousIndex !== event.currentIndex && isSameSection) {
            const onSuccess = () => {
                this.driveInstructionsDrawerService.getTourPointsData(transportOrderId, false);

                this.calculateRoutesService.triggerSilentCalculateRoutesSubject.next(
                    Number(transportOrderId)
                );
            };

            this.dragDropService.dropAssigmentLegsReorder(
                transportOrderId,
                this.detailsTourPoints()[event.previousIndex].legId,
                event.currentIndex,
                onSuccess
            );
        }
    }

    canSortToIndex = (index: number, drag: CdkDrag<LegTourPointConfig>): boolean => {
        return !!this.detailsTourPoints()[index]?.staysLoaded === !!drag.data.staysLoaded;
    };

    private isSameStaysLoadedSection(previousIndex: number, currentIndex: number): boolean {
        const draggedLeg = this.detailsTourPoints()[previousIndex];
        const targetLeg = this.detailsTourPoints()[currentIndex];

        return !!draggedLeg && !!targetLeg && !!draggedLeg.staysLoaded === !!targetLeg.staysLoaded;
    }

    shouldDisableReordering(): boolean {
        const isNotLoadingPoint = this.driveInstructionsDrawerService.selectedTourPointSubject.value?.type !== PICKUP_TOUR_POINT_TYPE;
        const isCompleted = this.driveInstructionsDrawerService.transportOrderDataSubject.value.status === TransportOrderStatus.Completed;

        return isCompleted || isNotLoadingPoint || this.isDisabled();
    }

    getIconName(trafficIcon: string) {
        return trafficIcon === 'ArrowUp' ? 'circle_arrow_up' : 'circle_arrow_down';
    }

    hasItemsSelectedForRemoval(): boolean {
        return this.detailsTourPoints().some((legTourPoint: LegTourPointConfig) =>  legTourPoint.checked);
    }

    removeSelected(): void {
        if (this.hasItemsSelectedForRemoval()) {
            const selectedLegs = this.detailsTourPoints().filter((legTourPoint: LegTourPointConfig) => legTourPoint.checked);

            const onSuccess = () => {
                const { transportOrderId } = this.driveInstructionsDrawerService.transportOrderDataSubject.value;
                this.driveInstructionsDrawerService.getTourPointsData(transportOrderId);
                this.driveInstructionsDrawerService.retrieveModifiedTransportOrderId();
                this.driveInstructionsDrawerService.isUnassigningTriggered.set(true);
                this.closeDetailsDrawer();
            };

            const dialogRef = this.dialog.open(ConfirmationDialogComponent, {
                data: DELETE_TO_CONFIRMATION_DIALOG_TEXT,
                width: '312px',
                height: '230px',
                panelClass: 'remove-panel-class'
            });

            dialogRef.afterClosed().subscribe((confirmed: boolean) => {
                confirmed && this.driveInstructionsDrawerService.unassignLegsAndLotsFromTransportOrder(
                    selectedLegs,
                    onSuccess
                );
            });
        }
    }
}